import { ResponseError, StyleCriterion, TaskTypeEnum } from '@api';
import { Button, FormError, FormLabel, FormRow, Input, NumberInputWide, RadioGroup, Textarea } from '@components/ui';
import { useApi } from '@data-access';
import { ErrorHelper } from '@helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import { STYLE_MAX_LEVELS, STYLE_MIN_LEVELS } from '@lib/styleMetric';
import { Resolver, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import styles from '../StyleStep.module.scss';

type ScaleMode = 'passFail' | 'levels';

type StyleCriterionFormData = {
  name: string;
  definition: string;
  example: string;
  scaleMode: ScaleMode;
  levels?: number;
};

const PASS_FAIL_SCALE = 2;
const LEVELS_MESSAGE = `Please enter a whole number from ${STYLE_MIN_LEVELS} to ${STYLE_MAX_LEVELS}`;

const schema = yup.object().shape({
  name: yup.string().trim().required('Please enter name'),
  definition: yup.string().trim().required('Please enter definition'),
  example: yup.string().trim().required('Please enter example'),
  scaleMode: yup.mixed<ScaleMode>().oneOf(['passFail', 'levels']).required(),
  levels: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || Number.isNaN(value) ? undefined : value))
    .when('scaleMode', {
      is: 'levels',
      then: levels =>
        levels
          .required(LEVELS_MESSAGE)
          .integer(LEVELS_MESSAGE)
          .min(STYLE_MIN_LEVELS, LEVELS_MESSAGE)
          .max(STYLE_MAX_LEVELS, LEVELS_MESSAGE),
    }),
});

const FORM_FIELDS = ['name', 'definition', 'example'] as const;

interface StyleCriterionFormProps {
  taskType: TaskTypeEnum;
  criterion?: StyleCriterion | null;
  closeModal: () => void;
  onSaved: (criterion: StyleCriterion, isNew: boolean) => void;
}

export const StyleCriterionForm: React.FC<StyleCriterionFormProps> = ({ taskType, criterion, closeModal, onSaved }) => {
  const { api } = useApi();
  const isEdit = !!criterion;
  const scale = criterion?.scale ?? PASS_FAIL_SCALE;

  const {
    handleSubmit,
    register,
    control,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<StyleCriterionFormData>({
    resolver: yupResolver(schema) as Resolver<StyleCriterionFormData>,
    defaultValues: {
      name: criterion?.name ?? '',
      definition: criterion?.definition ?? '',
      example: criterion?.example ?? '',
      scaleMode: scale === PASS_FAIL_SCALE ? 'passFail' : 'levels',
      levels: scale === PASS_FAIL_SCALE ? STYLE_MIN_LEVELS : scale,
    },
  });

  const scaleMode = watch('scaleMode');

  // Maps DRF 400 bodies onto the form. The duplicate (name, task_type) check is the only
  // object-level validator, so `non_field_errors` is shown on the name field.
  const showValidationErrors = async (response: Response) => {
    const body: Record<string, string | string[]> = await response.json();
    Object.entries(body).forEach(([key, messages]) => {
      const message = ([] as string[]).concat(messages)[0];
      if (key === 'non_field_errors') {
        setError('name', {
          type: 'server',
          message: 'A style criterion with this name already exists for this task type.',
        });
      } else if ((FORM_FIELDS as readonly string[]).includes(key)) {
        setError(key as (typeof FORM_FIELDS)[number], { type: 'server', message });
      } else {
        setError('root.server', { type: 'server', message });
      }
    });
  };

  const onFormSubmit = handleSubmit(async ({ name, definition, example, scaleMode: mode, levels }) => {
    const fields = {
      name: name.trim(),
      definition,
      example,
      scale: mode === 'passFail' ? PASS_FAIL_SCALE : levels,
    };

    try {
      const saved = isEdit
        ? await api.apiStyleCriteriaPartialUpdate({ id: criterion.id, patchedStyleCriterion: fields })
        : await api.apiStyleCriteriaCreate({ styleCriterion: { ...fields, taskType } });
      toast.success(isEdit ? 'Successfully updated style criterion' : 'Successfully added style criterion', {
        autoClose: 6000,
      });
      onSaved(saved, !isEdit);
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === 400) {
        await showValidationErrors(error.response);
        return;
      }
      ErrorHelper.handleErrorNotification(error);
    }
  });

  return (
    <div>
      <FormRow>
        <FormLabel
          id="name"
          className={styles.label}
        >
          <div>Name</div>
        </FormLabel>
        <Input
          props={{ ...register('name'), id: 'name' }}
          error={errors.name?.message}
        />
      </FormRow>
      <FormRow>
        <FormLabel
          id="definition"
          className={styles.label}
        >
          <div>Definition</div>
        </FormLabel>
        <Textarea
          props={{
            ...register('definition'),
            id: 'definition',
            placeholder: 'Describe the style the outputs should follow',
          }}
          error={errors.definition?.message}
          className={styles.textarea}
          autoExpand
          minRows={4}
          maxRows={10}
        />
      </FormRow>
      <FormRow>
        <FormLabel
          id="example"
          className={styles.label}
        >
          <div>Example</div>
        </FormLabel>
        <Textarea
          props={{ ...register('example'), id: 'example', placeholder: 'An output written in the desired style' }}
          error={errors.example?.message}
          className={styles.textarea}
          autoExpand
          minRows={4}
          maxRows={10}
        />
      </FormRow>
      <FormRow>
        <FormLabel
          id="scaleMode"
          className={styles.label}
        >
          <div>Scale</div>
        </FormLabel>
        <div className={styles.scaleRow}>
          <RadioGroup
            control={control}
            inputProps={{ name: 'scaleMode' }}
            className={styles.scaleRadio}
            options={[
              { id: 'passFail', name: 'Pass / fail' },
              { id: 'levels', name: 'Levels' },
            ]}
          />
          {scaleMode === 'levels' && (
            <NumberInputWide
              className={styles.levelsInput}
              inputProps={{
                ...register('levels'),
                id: 'levels',
                min: STYLE_MIN_LEVELS,
                max: STYLE_MAX_LEVELS,
                placeholder: `${STYLE_MIN_LEVELS}–${STYLE_MAX_LEVELS}`,
              }}
              error={errors.levels?.message}
            />
          )}
        </div>
      </FormRow>

      {isEdit && (
        <p className={styles.note}>
          Changes apply to every assessment that uses this criterion. Past results keep a snapshot of the criterion as
          it was when they ran.
        </p>
      )}
      <FormError error={errors.root?.server?.message} />

      <div className={styles.buttons}>
        <Button
          type="button"
          view="secondary"
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onFormSubmit}
          disabled={isSubmitting}
        >
          {isEdit ? 'Save changes' : 'Add'}
        </Button>
      </div>
    </div>
  );
};
